import Promptable from 'UIComponents/decorators/Promptable';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import { H2 } from 'UIComponents/elements/headings';
import UILink from 'UIComponents/link/UILink';
import Code from 'UIComponents/elements/Code';
import Small from 'UIComponents/elements/Small';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import { useState } from 'react';
// @ts-expect-error untyped
import { responseError } from 'hub-http/helpers/response';
import { onResponse } from 'hub-http/middlewares/core';
import enviro from 'enviro';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const alertStore = FloatingAlertStore.newFloatingAlertStore();

// We dont want to repeatedly show this modal. Just once is fine.
let hasShownSOX403Error = false;
const formatVersion = version => {
  if (version == null) {
    return 'unknown';
  }
  if (version === 'static') {
    return 'dev';
  }
  return version.replace('static-', '');
};
function isSOXFailure(response) {
  const data = response.responseJSON;
  return response.status === 403 && data && (data.decision === 'DENY_PENDING_APPROVAL' || data.decision === 'DENY');
}
const SmallCode = ({
  children
}) => /*#__PURE__*/_jsx(Small, {
  children: /*#__PURE__*/_jsx(Code, {
    children: children
  })
});
const SlackLink = ({
  channelId,
  channelName
}) => /*#__PURE__*/_jsxs(UILink, {
  external: true,
  href: `https://hubspot.enterprise.slack.com/archives/${channelId}`,
  children: ["#", channelName]
});
const ApprovalRequestedMessage = () => /*#__PURE__*/_jsxs(_Fragment, {
  children: ["Your request has been submitted for approval. Please check", ' ', /*#__PURE__*/_jsx(SlackLink, {
    channelId: "C087LNRPQP5",
    channelName: "sox-puppet-approvals"
  }), " for next steps."]
});
const ErrorMessage = () => /*#__PURE__*/_jsxs(_Fragment, {
  children: ["There was an error requesting approval for this request. Please reach out in", ' ', /*#__PURE__*/_jsx(SlackLink, {
    channelId: "C07KJAJMVMX",
    channelName: "product-wide-sox-questions"
  }), ' ', "for support."]
});
const SoxModal = ({
  url,
  correlationId,
  reason,
  onConfirm,
  requestOptions,
  canBeApproved
}) => {
  const [isLoading, setLoading] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('NOT_REQUESTED');
  const makeApprovalRequest = async () => {
    try {
      setLoading(true);
      const {
        appInfo,
        method,
        data,
        withCredentials
      } = requestOptions;

      // We need to rebuild the original request from the hub-http options, unfortunately.
      let requestUrl = requestOptions.url;
      if (appInfo) {
        try {
          const parsedUrl = new URL(requestUrl);
          requestUrl.searchParams.append('hs_static_app', appInfo.name);
          requestUrl.searchParams.append('hs_static_app_version', formatVersion(appInfo.version));
          requestUrl = parsedUrl.toString();
        } catch (e) {
          // do nothing, fallback to the param-less URL.
        }
      }
      await fetch(requestUrl, {
        method,
        headers: Object.assign({}, requestOptions.headers, {
          'X-Hubspot-Request-Approval': 'true'
        }),
        body: data,
        credentials: withCredentials ? 'include' : 'same-origin'
      });
      alertStore.addAlert({
        message: /*#__PURE__*/_jsx(ApprovalRequestedMessage, {}),
        timestamp: Date.now(),
        type: 'success',
        titleText: 'Approval Requested!',
        use: 'inline',
        timeout: 30000
      });
      setApprovalStatus('REQUESTED');
      setLoading(false);
    } catch (error) {
      console.error('Error requesting SOX approval:', error);
      alertStore.addAlert({
        message: /*#__PURE__*/_jsx(ErrorMessage, {}),
        timestamp: Date.now(),
        type: 'danger',
        titleText: 'Error!',
        use: 'inline',
        timeout: 30000
      });
      setApprovalStatus('ERROR');
      setLoading(false);
    }
  };
  return /*#__PURE__*/_jsxs(UIModal, {
    use: "danger",
    width: 600,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: "SOX Compliance Error!"
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: () => onConfirm()
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [/*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(UIFloatingAlertList, {
          alertStore: alertStore,
          use: "contextual"
        }), "Received a 403 ", /*#__PURE__*/_jsx(SmallCode, {
          children: reason
        }), " error from", ' ', /*#__PURE__*/_jsx(UIScrollContainer, {
          children: /*#__PURE__*/_jsx("div", {
            style: {
              whiteSpace: 'nowrap',
              height: '35px'
            },
            children: /*#__PURE__*/_jsx(SmallCode, {
              children: url
            })
          })
        })]
      }), /*#__PURE__*/_jsxs("div", {
        children: ["This indicates a SOX Compliance Issue. Please check the console for further errors due to SOX. For more information, please see", ' ', /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: "https://product.hubteam.com/docs/product-wide-sox/index.html",
          children: "the Product Wide SOX documentation"
        }), ".", !canBeApproved && /*#__PURE__*/_jsx("div", {
          style: {
            textAlign: 'center'
          },
          children: /*#__PURE__*/_jsxs("strong", {
            children: ["You cannot request approval for this request. Please reach out in", ' ', /*#__PURE__*/_jsx(SlackLink, {
              channelId: "C07KJAJMVMX",
              channelName: "product-wide-sox-questions"
            }), ' ', "for more info."]
          })
        })]
      }), /*#__PURE__*/_jsx("div", {
        style: {
          textAlign: 'center'
        },
        children: /*#__PURE__*/_jsxs(Small, {
          children: ["Correlation ID: ", /*#__PURE__*/_jsx(SmallCode, {
            children: correlationId
          })]
        })
      })]
    }), /*#__PURE__*/_jsx(UIDialogFooter, {
      align: "center",
      children: /*#__PURE__*/_jsxs(UILoadingButton, {
        loading: isLoading,
        onClick: () => {
          makeApprovalRequest().catch(() => {
            // Do nothing
          });
        },
        disabled: !canBeApproved || approvalStatus !== 'NOT_REQUESTED',
        use: "primary",
        children: ["Request Approval (", enviro.isProd() ? 'Prod' : 'QA', ")"]
      })
    })]
  });
};
async function handleSOX403Error(response, options) {
  var _response$options$url;
  const url = (_response$options$url = response.options.url) === null || _response$options$url === void 0 ? void 0 : _response$options$url.split('?')[0];
  const correlationId = response.headers['x-hubspot-correlation-id'];
  const decision = response.responseJSON.decision;
  const reason = response.responseJSON.reason;
  // this is already a 403 for sure, so all we need to check is the reason
  const canBeApproved = decision === 'DENY_PENDING_APPROVAL';
  const message = `Received a 403 ${reason} from ${url}, indicating a SOX Compliance Violation. Correlation Id: ${correlationId}. \n\nPlease see https://product.hubteam.com/docs/product-wide-sox/index.html or #product-wide-sox-questions for more information.`;
  console.error(message);
  if (!hasShownSOX403Error || options !== null && options !== void 0 && options.allowMultipleSoxModals) {
    hasShownSOX403Error = true;
    try {
      await Promptable(({
        onConfirm
      }) => /*#__PURE__*/_jsx(SoxModal, {
        canBeApproved: canBeApproved,
        onConfirm: onConfirm,
        url: url,
        correlationId: correlationId,
        reason: reason,
        requestOptions: options
      }))();
    } catch (e) {
      // do nothing
    }
  }
  return Promise.reject(responseError(response, message));
}
const addQueryParamToResponseError = (response, url) => {
  const error = responseError(response, `Request for ${url.split('?')[0]} failed with status ${response.status}. ${response.statusText || ''}`);
  error._hsAdditionalProperties = {
    queryParamsString: url.split('?')[1]
  };
  return error;
};
export const validateStatus = options => onResponse(response => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  if (isSOXFailure(response)) {
    return handleSOX403Error(response, options);
  }
  return Promise.reject(addQueryParamToResponseError(response, options.url));
})(options);